const DEFAULT_MAX_BYTES = 900 * 1024;
const DEFAULT_MAX_EDGE = 1920;
const DEFAULT_QUALITY = 0.82;
const DECODE_TIMEOUT_MS = 45000;
const MIN_QUALITY = 0.45;

function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    Promise.resolve(promise).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

/** Browsers that can't encode WebP silently return PNG from toBlob — detect that via the result type. */
function isWebpBlob(blob) {
  return Boolean(blob) && blob.type === "image/webp";
}

/** GIF / SVG are never re-encoded (animation / vector would be lost). */
function isSkippedType(file) {
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return (
    type === "image/gif" ||
    type === "image/svg+xml" ||
    name.endsWith(".gif") ||
    name.endsWith(".svg") ||
    name.endsWith(".svgz")
  );
}

/** Animated PNG (acTL chunk) / animated WebP (ANIM chunk): a canvas would flatten them to one frame. */
async function isAnimatedRaster(file) {
  const type = String(file.type || "").toLowerCase();
  if (type !== "image/png" && type !== "image/webp") return false;
  try {
    const text = new TextDecoder("latin1").decode(await file.slice(0, 64 * 1024).arrayBuffer());
    return type === "image/png" ? text.includes("acTL") : text.includes("ANIM");
  } catch {
    return false;
  }
}

async function decode(file) {
  try {
    // Apply the camera's EXIF rotation (default in modern browsers; explicit for the rest).
    return await withTimeout(createImageBitmap(file, { imageOrientation: "from-image" }), DECODE_TIMEOUT_MS, "decode");
  } catch (e) {
    if (String(e?.message || "").includes("timed out")) throw e;
    return withTimeout(createImageBitmap(file), DECODE_TIMEOUT_MS, "decode");
  }
}

/**
 * Downscale (never enlarge) an image and re-encode it as WebP before upload.
 * Returns the ORIGINAL file (the backend then optimises it) when: it isn't an image, it is a GIF/SVG or an
 * animated PNG/WebP, the browser can't encode WebP, decoding fails, or the WebP isn't actually smaller.
 *
 * @param {File} file
 * @param {{ maxEdge?: number, maxWidth?: number, maxBytes?: number, quality?: number }} [options]
 *   maxEdge caps the longest side; maxWidth (optional) additionally caps the width (tall mobile banners).
 * @returns {Promise<File>}
 */
export async function compressUploadImage(file, options = {}) {
  if (!file || !String(file.type || "").startsWith("image/")) return file;
  if (isSkippedType(file)) return file;

  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE;
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  let quality = options.quality ?? DEFAULT_QUALITY;

  let bitmap;
  try {
    if (await isAnimatedRaster(file)) return file;
    bitmap = await decode(file);
    const scale = Math.min(
      1,
      maxEdge / Math.max(bitmap.width, bitmap.height),
      options.maxWidth ? options.maxWidth / bitmap.width : 1
    );
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);

    let blob = await canvasToBlob(canvas, "image/webp", quality);
    if (!isWebpBlob(blob)) return file; // no WebP encoder -> let the backend do it
    while (blob.size > maxBytes && quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality - 0.1);
      const next = await canvasToBlob(canvas, "image/webp", quality);
      if (!isWebpBlob(next)) break;
      blob = next;
    }
    const resized = scale < 1;
    // Re-encoding can only help if it got smaller (or we had to downscale anyway).
    if (!resized && blob.size >= file.size) return file;
    const base = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.webp`, { type: "image/webp", lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    bitmap?.close?.();
  }
}
